import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Heart, Landmark, Leaf, Award, ArrowRight, MapPin } from 'lucide-react';
import Header from './components/Header22';
import Footer from './components/Footer22';

const About22 = () => {
    const navigate = useNavigate();

    const values = [
        {
            icon: Heart,
            title: 'Artisan First',
            desc: 'Every purchase directly supports the artisan who made it. We ensure fair wages, safe working conditions, and recognition for every maker in our network of over 200 craft families across India.'
        },
        {
            icon: Landmark,
            title: 'Heritage Craft',
            desc: 'From Banarasi brocades to Ajrakh block prints, we work with craft forms that have thrived for centuries. Our mission is to keep these traditions alive for future generations.'
        },
        {
            icon: Leaf,
            title: 'Sustainable Fashion',
            desc: 'Handloom is inherently sustainable \u2014 zero carbon footprint, natural dyes, and biodegradable fabrics. We champion slow fashion that respects both maker and planet.'
        },
        {
            icon: Award,
            title: 'Authentic Quality',
            desc: 'Every piece undergoes a rigorous quality check and carries an authenticity certificate tracing it back to the artisan and region of origin. No machine reproductions, ever.'
        },
    ];

    const milestones = [
        { year: '2018', title: 'The First Thread', desc: 'Founded in a small Jaipur studio with 5 block print artisans and a dream to bring authentic handcraft to modern wardrobes.' },
        { year: '2019', title: 'Weaver Collective', desc: 'Partnered with Chanderi and Maheshwari weaver cooperatives in Madhya Pradesh, expanding our artisan network to 50 families.' },
        { year: '2020', title: 'Digital Loom', desc: 'Launched our online platform during the pandemic, helping artisans reach customers when local markets closed.' },
        { year: '2022', title: 'Craft Trails', desc: 'Introduced craft trail experiences, connecting customers with artisans in their villages across Rajasthan, Gujarat, and UP.' },
        { year: '2024', title: 'GI Tag Champions', desc: 'Became the first e-commerce platform to mandate GI (Geographical Indication) certification for all listed crafts.' },
        { year: '2026', title: '200 Families Strong', desc: 'Our artisan network now spans 12 states, 28 craft forms, and supports 200+ artisan families with sustainable livelihoods.' },
    ];

    const stats = [
        { number: '200+', label: 'Artisan Families' },
        { number: '28', label: 'Craft Forms' },
        { number: '12', label: 'States' },
        { number: '15,000+', label: 'Happy Customers' },
    ];

    const regions = [
        { name: 'Varanasi, UP', craft: 'Banarasi Weaving', image: 'https://images.pexels.com/photos/9419032/pexels-photo-9419032.jpeg?auto=compress&cs=tinysrgb&w=600' },
        { name: 'Jaipur, Rajasthan', craft: 'Block Printing', image: 'https://images.pexels.com/photos/8838890/pexels-photo-8838890.jpeg?auto=compress&cs=tinysrgb&w=600' },
        { name: 'Lucknow, UP', craft: 'Chikankari', image: 'https://images.pexels.com/photos/7123307/pexels-photo-7123307.jpeg?auto=compress&cs=tinysrgb&w=600' },
        { name: 'Kutch, Gujarat', craft: 'Ajrakh & Bandhani', image: 'https://images.pexels.com/photos/6764040/pexels-photo-6764040.jpeg?auto=compress&cs=tinysrgb&w=600' },
        { name: 'Chanderi, MP', craft: 'Chanderi Weaving', image: 'https://images.pexels.com/photos/10679171/pexels-photo-10679171.jpeg?auto=compress&cs=tinysrgb&w=600' },
        { name: 'Pochampally, Telangana', craft: 'Ikat Weaving', image: 'https://images.pexels.com/photos/6764007/pexels-photo-6764007.jpeg?auto=compress&cs=tinysrgb&w=600' },
    ];

    const weavingProcess = [
        { step: '01', title: 'Sourcing', desc: 'Raw materials are ethically sourced \u2014 organic cotton from Gujarat, pure silk from Karnataka, and natural dyes from indigenous plants.' },
        { step: '02', title: 'Design', desc: 'Our design team collaborates with artisans to create contemporary silhouettes while preserving traditional motifs and techniques.' },
        { step: '03', title: 'Crafting', desc: 'Each piece is handcrafted using traditional tools \u2014 pit looms, wooden blocks, and hand needles. No machines, no shortcuts.' },
        { step: '04', title: 'Quality Check', desc: 'Every product undergoes rigorous inspection for weave consistency, colour fastness, and finish before receiving our authenticity tag.' },
    ];

    return (
        <>
            <style>{`
                @import url('https://fonts.googleapis.com/css2?family=Cormorant+Infant:ital,wght@0,300;0,400;0,500;0,600;0,700;1,400&family=Source+Sans+3:wght@300;400;500;600;700&display=swap');

                .maati-about-grid {
                    display: grid;
                    grid-template-columns: 1fr;
                    gap: 20px;
                }
                @media (min-width: 768px) {
                    .maati-about-grid {
                        grid-template-columns: repeat(2, 1fr);
                    }
                }
                @media (min-width: 1024px) {
                    .maati-about-grid {
                        grid-template-columns: repeat(4, 1fr);
                    }
                }
                .maati-region-grid {
                    display: grid;
                    grid-template-columns: repeat(2, 1fr);
                    gap: 16px;
                }
                @media (min-width: 768px) {
                    .maati-region-grid {
                        grid-template-columns: repeat(3, 1fr);
                    }
                }
            `}</style>

            <div style={{ minHeight: '100vh', backgroundColor: '#FAF6F1', fontFamily: "'Source Sans 3', -apple-system, sans-serif" }}>
                <Header />

                <main>
                    {/* Hero Section */}
                    <section style={{
                        position: 'relative', width: '100%', minHeight: '65vh',
                        overflow: 'hidden', display: 'flex', alignItems: 'center',
                    }}>
                        <img
                            src="https://images.pexels.com/photos/6764040/pexels-photo-6764040.jpeg?auto=compress&cs=tinysrgb&w=1920"
                            alt="Indian handloom artisan"
                            style={{
                                position: 'absolute', inset: 0, width: '100%', height: '100%',
                                objectFit: 'cover',
                            }}
                        />
                        <div style={{
                            position: 'absolute', inset: 0,
                            background: 'linear-gradient(to right, rgba(250,246,241,0.95) 0%, rgba(250,246,241,0.7) 50%, rgba(250,246,241,0.3) 100%)',
                        }} />
                        <div style={{
                            position: 'relative', zIndex: 1, maxWidth: '1200px',
                            margin: '0 auto', padding: '140px 48px 80px', width: '100%',
                        }}>
                            <div style={{ maxWidth: '600px' }}>
                                <div style={{ width: '48px', height: '2px', backgroundColor: '#C4572A', marginBottom: '24px' }} />
                                <p style={{
                                    fontSize: '13px', fontWeight: 500, color: '#C4572A',
                                    letterSpacing: '3px', textTransform: 'uppercase', marginBottom: '16px',
                                }}>
                                    Our Story
                                </p>
                                <h1 style={{
                                    fontFamily: "'Cormorant Infant', Georgia, serif",
                                    fontSize: 'clamp(40px, 6vw, 64px)', fontWeight: 400,
                                    color: '#5C3D2E', lineHeight: 1.1, marginBottom: '20px',
                                }}>
                                    Born from the Earth, Woven with Soul
                                </h1>
                                <p style={{ fontSize: '17px', color: '#8B7355', lineHeight: 1.7, maxWidth: '500px' }}>
                                    MAATI is a celebration of India's living textile heritage. We connect you directly with the artisan hands that weave, print, and embroider each piece of wearable art.
                                </p>
                            </div>
                        </div>
                    </section>

                    {/* Founder Story */}
                    <section style={{ maxWidth: '1200px', margin: '0 auto', padding: '80px 24px' }}>
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: '48px', alignItems: 'center' }}>
                            <div style={{ maxWidth: '800px', margin: '0 auto', textAlign: 'center' }}>
                                <p style={{
                                    fontSize: '12px', fontWeight: 500, color: '#C4572A',
                                    letterSpacing: '4px', textTransform: 'uppercase', marginBottom: '16px',
                                }}>
                                    The Founder
                                </p>
                                <h2 style={{
                                    fontFamily: "'Cormorant Infant', Georgia, serif",
                                    fontSize: '36px', fontWeight: 500, color: '#5C3D2E', marginBottom: '24px',
                                }}>
                                    "Every thread tells a story. Every weave carries a prayer."
                                </h2>
                                <p style={{ fontSize: '16px', color: '#8B7355', lineHeight: 1.8, marginBottom: '16px' }}>
                                    MAATI was born from a journey through the weaver villages of India. Watching master artisans create magic on century-old looms, using techniques passed down through generations, I realized that this incredible heritage was slowly fading away. Fast fashion was replacing slow craft. Machine-made was replacing handmade.
                                </p>
                                <p style={{ fontSize: '16px', color: '#8B7355', lineHeight: 1.8, marginBottom: '24px' }}>
                                    MAATI was founded to bridge this gap \u2014 to bring the beauty of authentic Indian handcraft to modern wardrobes, while ensuring that the artisans who create these masterpieces receive fair recognition and sustainable livelihoods. The name "MAATI" means earth \u2014 because every craft we work with is rooted in the soil, culture, and soul of its region.
                                </p>
                                <p style={{
                                    fontFamily: "'Cormorant Infant', Georgia, serif",
                                    fontSize: '20px', fontStyle: 'italic', color: '#5C3D2E',
                                }}>
                                    \u2014 The MAATI Team
                                </p>
                            </div>
                        </div>
                    </section>

                    {/* Values */}
                    <section style={{ backgroundColor: '#F3ECE4', padding: '80px 24px' }}>
                        <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
                            <div style={{ textAlign: 'center', marginBottom: '48px' }}>
                                <p style={{
                                    fontSize: '12px', fontWeight: 500, color: '#C4572A',
                                    letterSpacing: '4px', textTransform: 'uppercase', marginBottom: '12px',
                                }}>
                                    What We Stand For
                                </p>
                                <h2 style={{
                                    fontFamily: "'Cormorant Infant', Georgia, serif",
                                    fontSize: '40px', fontWeight: 400, color: '#5C3D2E',
                                }}>
                                    Our Values
                                </h2>
                            </div>
                            <div className="maati-about-grid">
                                {values.map((value, i) => (
                                    <div key={i} style={{
                                        backgroundColor: '#FFFFFF', borderRadius: '12px',
                                        padding: '32px 24px', textAlign: 'center',
                                        border: '1px solid #E8DDD0',
                                        transition: 'transform 0.3s ease, box-shadow 0.3s ease',
                                    }}
                                    onMouseEnter={(e) => {
                                        e.currentTarget.style.transform = 'translateY(-4px)';
                                        e.currentTarget.style.boxShadow = '0 12px 40px rgba(92,61,46,0.1)';
                                    }}
                                    onMouseLeave={(e) => {
                                        e.currentTarget.style.transform = 'none';
                                        e.currentTarget.style.boxShadow = 'none';
                                    }}
                                    >
                                        <div style={{
                                            width: '56px', height: '56px', borderRadius: '50%',
                                            backgroundColor: '#FFF0EB', display: 'flex',
                                            alignItems: 'center', justifyContent: 'center',
                                            margin: '0 auto 20px',
                                        }}>
                                            <value.icon size={24} color="#C4572A" />
                                        </div>
                                        <h3 style={{
                                            fontFamily: "'Cormorant Infant', Georgia, serif",
                                            fontSize: '22px', fontWeight: 600, color: '#5C3D2E',
                                            margin: '0 0 12px 0',
                                        }}>
                                            {value.title}
                                        </h3>
                                        <p style={{ fontSize: '14px', color: '#8B7355', lineHeight: 1.6, margin: 0 }}>
                                            {value.desc}
                                        </p>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </section>

                    {/* Stats */}
                    <section style={{ padding: '60px 24px', backgroundColor: '#FAF6F1' }}>
                        <div style={{
                            maxWidth: '900px', margin: '0 auto',
                            display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '24px',
                            textAlign: 'center',
                        }}>
                            {stats.map((stat, i) => (
                                <div key={i}>
                                    <p style={{
                                        fontFamily: "'Cormorant Infant', Georgia, serif",
                                        fontSize: '42px', fontWeight: 700, color: '#C4572A',
                                        margin: '0 0 4px 0',
                                    }}>
                                        {stat.number}
                                    </p>
                                    <p style={{ fontSize: '14px', color: '#8B7355', margin: 0, fontWeight: 500 }}>
                                        {stat.label}
                                    </p>
                                </div>
                            ))}
                        </div>
                    </section>

                    {/* Weaving Process */}
                    <section style={{ maxWidth: '1200px', margin: '0 auto', padding: '80px 24px' }}>
                        <div style={{ textAlign: 'center', marginBottom: '48px' }}>
                            <p style={{
                                fontSize: '12px', fontWeight: 500, color: '#C4572A',
                                letterSpacing: '4px', textTransform: 'uppercase', marginBottom: '12px',
                            }}>
                                From Loom to You
                            </p>
                            <h2 style={{
                                fontFamily: "'Cormorant Infant', Georgia, serif",
                                fontSize: '40px', fontWeight: 400, color: '#5C3D2E',
                            }}>
                                Our Process
                            </h2>
                        </div>
                        <div style={{
                            display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
                            gap: '32px',
                        }}>
                            {weavingProcess.map((item, i) => (
                                <div key={i} style={{ position: 'relative' }}>
                                    <span style={{
                                        fontFamily: "'Cormorant Infant', Georgia, serif",
                                        fontSize: '64px', fontWeight: 700, color: '#F3ECE4',
                                        position: 'absolute', top: '-10px', left: '0',
                                        lineHeight: 1, zIndex: 0,
                                    }}>
                                        {item.step}
                                    </span>
                                    <div style={{ position: 'relative', zIndex: 1, paddingTop: '40px', paddingLeft: '8px' }}>
                                        <h3 style={{
                                            fontFamily: "'Cormorant Infant', Georgia, serif",
                                            fontSize: '22px', fontWeight: 600, color: '#5C3D2E',
                                            margin: '0 0 10px 0',
                                        }}>
                                            {item.title}
                                        </h3>
                                        <p style={{ fontSize: '14px', color: '#8B7355', lineHeight: 1.7, margin: 0 }}>
                                            {item.desc}
                                        </p>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </section>

                    {/* Heritage Timeline */}
                    <section style={{ backgroundColor: '#F3ECE4', padding: '80px 24px' }}>
                        <div style={{ maxWidth: '900px', margin: '0 auto' }}>
                            <div style={{ textAlign: 'center', marginBottom: '48px' }}>
                                <p style={{
                                    fontSize: '12px', fontWeight: 500, color: '#C4572A',
                                    letterSpacing: '4px', textTransform: 'uppercase', marginBottom: '12px',
                                }}>
                                    Our Journey
                                </p>
                                <h2 style={{
                                    fontFamily: "'Cormorant Infant', Georgia, serif",
                                    fontSize: '40px', fontWeight: 400, color: '#5C3D2E',
                                }}>
                                    Craft Heritage Timeline
                                </h2>
                            </div>
                            <div style={{ position: 'relative' }}>
                                {/* Timeline line */}
                                <div style={{
                                    position: 'absolute', left: '24px', top: '0', bottom: '0',
                                    width: '2px', backgroundColor: '#E8DDD0',
                                }} />
                                {milestones.map((milestone, i) => (
                                    <div key={i} style={{
                                        display: 'flex', gap: '32px', marginBottom: i < milestones.length - 1 ? '40px' : '0',
                                        position: 'relative',
                                    }}>
                                        <div style={{
                                            width: '50px', height: '50px', borderRadius: '50%',
                                            backgroundColor: '#C4572A', display: 'flex',
                                            alignItems: 'center', justifyContent: 'center',
                                            flexShrink: 0, zIndex: 1,
                                            boxShadow: '0 0 0 6px #F3ECE4',
                                        }}>
                                            <span style={{
                                                fontSize: '13px', fontWeight: 700, color: '#FFFFFF',
                                            }}>
                                                {milestone.year.slice(-2)}
                                            </span>
                                        </div>
                                        <div style={{ paddingTop: '4px' }}>
                                            <div style={{ display: 'flex', alignItems: 'baseline', gap: '12px', marginBottom: '6px' }}>
                                                <span style={{ fontSize: '14px', fontWeight: 600, color: '#C4572A' }}>
                                                    {milestone.year}
                                                </span>
                                                <h3 style={{
                                                    fontFamily: "'Cormorant Infant', Georgia, serif",
                                                    fontSize: '22px', fontWeight: 600, color: '#5C3D2E', margin: 0,
                                                }}>
                                                    {milestone.title}
                                                </h3>
                                            </div>
                                            <p style={{ fontSize: '14px', color: '#8B7355', lineHeight: 1.6, margin: 0 }}>
                                                {milestone.desc}
                                            </p>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </section>

                    {/* Sourcing Regions */}
                    <section style={{ maxWidth: '1200px', margin: '0 auto', padding: '80px 24px' }}>
                        <div style={{ textAlign: 'center', marginBottom: '48px' }}>
                            <p style={{
                                fontSize: '12px', fontWeight: 500, color: '#C4572A',
                                letterSpacing: '4px', textTransform: 'uppercase', marginBottom: '12px',
                            }}>
                                Where We Source
                            </p>
                            <h2 style={{
                                fontFamily: "'Cormorant Infant', Georgia, serif",
                                fontSize: '40px', fontWeight: 400, color: '#5C3D2E',
                            }}>
                                Our Artisan Regions
                            </h2>
                        </div>
                        <div className="maati-region-grid">
                            {regions.map((region, i) => (
                                <div key={i} style={{
                                    borderRadius: '12px', overflow: 'hidden',
                                    position: 'relative', aspectRatio: '4/3',
                                    cursor: 'pointer',
                                }}
                                onMouseEnter={(e) => {
                                    e.currentTarget.querySelector('img').style.transform = 'scale(1.05)';
                                }}
                                onMouseLeave={(e) => {
                                    e.currentTarget.querySelector('img').style.transform = 'scale(1)';
                                }}
                                >
                                    <img
                                        src={region.image}
                                        alt={region.name}
                                        style={{
                                            width: '100%', height: '100%', objectFit: 'cover',
                                            transition: 'transform 0.6s ease',
                                        }}
                                    />
                                    <div style={{
                                        position: 'absolute', inset: 0,
                                        background: 'linear-gradient(to top, rgba(92,61,46,0.85) 0%, rgba(92,61,46,0.2) 60%, transparent 100%)',
                                    }} />
                                    <div style={{
                                        position: 'absolute', bottom: '20px', left: '20px', right: '20px',
                                    }}>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '4px' }}>
                                            <MapPin size={14} color="#F3ECE4" />
                                            <p style={{ fontSize: '14px', fontWeight: 600, color: '#FFFFFF', margin: 0 }}>
                                                {region.name}
                                            </p>
                                        </div>
                                        <p style={{ fontSize: '13px', color: '#F3ECE4', margin: 0, opacity: 0.85 }}>
                                            {region.craft}
                                        </p>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </section>

                    {/* CTA */}
                    <section style={{
                        backgroundColor: '#5C3D2E', padding: '80px 24px', textAlign: 'center',
                    }}>
                        <div style={{ maxWidth: '600px', margin: '0 auto' }}>
                            <h2 style={{
                                fontFamily: "'Cormorant Infant', Georgia, serif",
                                fontSize: '40px', fontWeight: 400, color: '#FAF6F1', marginBottom: '16px',
                            }}>
                                Wear the Heritage
                            </h2>
                            <p style={{ fontSize: '16px', color: '#E8DDD0', lineHeight: 1.7, marginBottom: '32px' }}>
                                Every piece you buy supports an artisan family and keeps a centuries-old craft tradition alive.
                            </p>
                            <button
                                onClick={() => navigate('/template22/shop')}
                                style={{
                                    padding: '16px 40px', backgroundColor: '#C4572A',
                                    color: '#FFFFFF', border: 'none', borderRadius: '8px',
                                    fontSize: '15px', fontWeight: 600, letterSpacing: '1px',
                                    textTransform: 'uppercase', cursor: 'pointer',
                                    fontFamily: "'Source Sans 3', sans-serif",
                                    display: 'inline-flex', alignItems: 'center', gap: '8px',
                                    transition: 'all 0.3s ease',
                                }}
                                onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = '#D4683B'; }}
                                onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = '#C4572A'; }}
                            >
                                Explore Collection <ArrowRight size={18} />
                            </button>
                        </div>
                    </section>
                </main>

                <Footer />
            </div>
        </>
    );
};

export default About22;
